import { type PricingPlan } from './data';

// Local pricing for the `multi_currency` Solid products, ported from the web
// app's `src/features/paywall/data/multiCurrencyPrices.ts` (WEB-4260).
//
// These are the amounts this page quotes. They are NOT converted from the USD
// base at runtime — each one mirrors the price configured for that country in
// the Solid product, otherwise the page would show one amount and the card
// would be charged another. They are copied from the Solid product
// configuration verbatim, including the thousands separators and however many
// decimals the currency uses (CLP and VND have none, KWD and JOD have three),
// because they are rendered as-is.
//
// The page only hands the checkout a product name (through the
// `plan_from_landing` cookie); Solid decides what to charge from that name, so
// quoting a local amount and selecting the matching `multi_currency` SKU have
// to happen together.
//
// All three products are priced in the same set of countries. A country that is
// missing from any of the tables is not offered the multi-currency plans at all
// — see `getMultiCurrencyPricingPlans` below.

// Product names, as in the web app's `PAYWALL_PLANS`.
const MULTI_CURRENCY_PRODUCTS = {
	MONTH: 'liftapp.ai_1month_multi_currency_49.99USD_no_trial',
	MONTH_TRIAL: 'liftapp.ai_1month_multi_currency_49.99USD_1week_1USD_trial',
	QUARTER: 'liftapp.ai_3month_multi_currency_89.99USD_no_trial',
	YEAR: 'liftapp.ai_1year_multi_currency_199.99USD_no_trial',
} as const;

// The currency each country is charged in.
const COUNTRY_CURRENCIES: Record<string, string> = {
	BR: 'BRL',
	MX: 'MXN',
	AR: 'ARS',
	CO: 'COP',
	PE: 'PEN',
	CL: 'CLP',
	UY: 'UYU',
	DO: 'DOP',
	CR: 'CRC',
	PA: 'PAB',
	PR: 'USD',
	EC: 'USD',
	IN: 'INR',
	ID: 'IDR',
	PH: 'PHP',
	PK: 'PKR',
	BD: 'BDT',
	MY: 'MYR',
	VN: 'VND',
	TH: 'THB',
	SG: 'SGD',
	TW: 'TWD',
	HK: 'HKD',
	ZA: 'ZAR',
	TZ: 'TZS',
	NG: 'NGN',
	KE: 'KES',
	MA: 'MAD',
	EG: 'EGP',
	TR: 'TRY',
	IL: 'ILS',
	SA: 'SAR',
	AE: 'AED',
	JO: 'JOD',
	KW: 'KWD',
	QA: 'QAR',
	KZ: 'KZT',
	UZ: 'UZS',
	GE: 'GEL',
	LK: 'LKR',
	GT: 'GTQ',
	HN: 'HNL',
	JM: 'JMD',
};

// What each currency is rendered with, prepended to the amount. Currencies
// whose sign is a bare `$` (or has no widely recognised sign) are prefixed so
// the amount can't be read as US dollars; the rest use their own sign.
// TODO(WEB-4260): worth a pass from marketing — these are display copy.
const CURRENCY_SYMBOLS: Record<string, string> = {
	USD: '$',
	BRL: 'R$',
	MXN: 'MX$',
	ARS: 'AR$',
	COP: 'CO$',
	PEN: 'S/',
	CLP: 'CL$',
	UYU: '$U',
	DOP: 'RD$',
	CRC: '₡',
	PAB: 'B/.',
	INR: '₹',
	IDR: 'Rp',
	PHP: '₱',
	PKR: 'Rs ',
	BDT: '৳',
	MYR: 'RM',
	VND: '₫',
	THB: '฿',
	SGD: 'S$',
	TWD: 'NT$',
	HKD: 'HK$',
	ZAR: 'R',
	TZS: 'TSh ',
	NGN: '₦',
	KES: 'KSh ',
	MAD: 'MAD ',
	EGP: 'E£',
	TRY: '₺',
	ILS: '₪',
	SAR: 'SAR ',
	AED: 'AED ',
	JOD: 'JOD ',
	KWD: 'KWD ',
	QAR: 'QAR ',
	KZT: '₸',
	UZS: 'UZS ',
	GEL: '₾',
	LKR: 'Rs ',
	GTQ: 'Q',
	HNL: 'L',
	JMD: 'J$',
};

// `liftapp.ai_1month_multi_currency_49.99USD_1week_1USD_trial`
// [trial fee, price the subscription renews at every month].
const PRICES_1_MONTH_49_99_TRIAL: Record<string, [string, string]> = {
	BR: ['4.99', '199.99'],
	MX: ['19.99', '599.99'],
	AR: ['1,499.99', '49,999.99'],
	CO: ['2,999.99', '99,999.99'],
	PE: ['4.99', '124.99'],
	CL: ['999', '34,999'],
	UY: ['39.99', '1,499.99'],
	DO: ['49.99', '1,999.99'],
	CR: ['499.99', '16,999.99'],
	PA: ['0.99', '39.99'],
	PR: ['0.99', '39.99'],
	EC: ['0.99', '39.99'],
	IN: ['99.99', '2,999.99'],
	ID: ['19,999.99', '599,999.99'],
	PH: ['99.99', '1,999.99'],
	PK: ['299.99', '9,999.99'],
	BD: ['99.99', '3,999.99'],
	MY: ['4.99', '129.99'],
	VN: ['29,999', '799,999'],
	TH: ['29.99', '999.99'],
	SG: ['0.99', '69.99'],
	TW: ['29.99', '1,499.99'],
	HK: ['9.99', '399.99'],
	ZA: ['14.99', '499.99'],
	TZ: ['2,499.99', '79,999.99'],
	NG: ['1,499.99', '44,999.99'],
	KE: ['149.99', '3,999.99'],
	MA: ['9.99', '299.99'],
	EG: ['49.99', '1,699.99'],
	TR: ['49.99', '1,699.99'],
	IL: ['2.99', '149.99'],
	SA: ['3.99', '199.99'],
	AE: ['3.99', '199.99'],
	JO: ['0.999', '34.999'],
	KW: ['0.999', '14.999'],
	QA: ['3.99', '199.99'],
	KZ: ['499.99', '15,999.99'],
	UZ: ['12,999.99', '399,999.99'],
	GE: ['2.99', '89.99'],
	LK: ['399.99', '9,999.99'],
	GT: ['7.99', '299.99'],
	HN: ['29.99', '999.99'],
	JM: ['199.99', '5,999.99'],
};

// `liftapp.ai_3month_multi_currency_89.99USD_no_trial`
// One amount, charged up front and again at every renewal.
const PRICES_3_MONTH_89_99: Record<string, string> = {
	BR: '399.99',
	MX: '1,249.99',
	AR: '99,999.99',
	CO: '225,999.99',
	PE: '249.99',
	CL: '69,999',
	UY: '2,999.99',
	DO: '4,499.99',
	CR: '32,999.99',
	PA: '74.99',
	PR: '74.99',
	EC: '74.99',
	IN: '6,999.99',
	ID: '1,299,999.99',
	PH: '4,499.99',
	PK: '19,999.99',
	BD: '8,999.99',
	MY: '299.99',
	VN: '1,999,999',
	TH: '2,499.99',
	SG: '99.99',
	TW: '2,499.99',
	HK: '599.99',
	ZA: '1,199.99',
	TZ: '199,999.99',
	NG: '99,999.99',
	KE: '9,999.99',
	MA: '699.99',
	EG: '3,799.99',
	TR: '3,499.99',
	IL: '224.99',
	SA: '299.99',
	AE: '299.99',
	JO: '54.999',
	KW: '24.999',
	QA: '299.99',
	KZ: '34,999.99',
	UZ: '899,999.99',
	GE: '199.99',
	LK: '24,999.99',
	GT: '599.99',
	HN: '1,999.99',
	JM: '11,999.99',
};

// `liftapp.ai_1year_multi_currency_199.99USD_no_trial`
const PRICES_1_YEAR_199_99: Record<string, string> = {
	BR: '999.99',
	MX: '3,299.99',
	AR: '284,999.99',
	CO: '599,999.99',
	PE: '649.99',
	CL: '179,999',
	UY: '7,849.99',
	DO: '11,499.99',
	CR: '84,999.99',
	PA: '199.99',
	PR: '199.99',
	EC: '199.99',
	IN: '14,999.99',
	ID: '3,499,999.99',
	PH: '11,999.99',
	PK: '49,999.99',
	BD: '22,999.99',
	MY: '799.99',
	VN: '4,999,999',
	TH: '6,499.99',
	SG: '249.99',
	TW: '5,999.99',
	HK: '1,499.99',
	ZA: '2,999.99',
	TZ: '499,999.99',
	NG: '249,999.99',
	KE: '24,999.99',
	MA: '1,799.99',
	EG: '9,999.99',
	TR: '9,999.99',
	IL: '599.99',
	SA: '749.99',
	AE: '749.99',
	JO: '134.999',
	KW: '59.999',
	QA: '699.99',
	KZ: '89,999.99',
	UZ: '2,299,999.99',
	GE: '499.99',
	LK: '64,999.99',
	GT: '1,499.99',
	HN: '4,999.99',
	JM: '29,999.99',
};

const toAmount = (price: string) => Number(price.replace(/,/g, ''));

// What a term price works out to per month — the `16.66 /month` a plan sold as
// 199.99 a year shows on the card. Display only: the legal copy under the plans
// keeps quoting the term, which is what is actually charged.
//
// Takes and returns the amount as a string, because local prices are configured
// per country rather than converted, and are rendered as configured: the
// currency's own precision and comma-grouped thousands.
const getPerMonthAmount = (price: string, months: number): string => {
	const amount = toAmount(price);

	if (!amount || months < 1) return price;

	const decimals = price.split('.')[1]?.length ?? 0;
	const factor = 10 ** decimals;
	// Truncated rather than rounded, so the figure never reads higher than the
	// term price divided out. The epsilon only absorbs binary representation
	// error, which would otherwise drop the last digit on an exact division.
	const perMonth = Math.floor((amount / months) * factor + 1e-6) / factor;

	return perMonth.toLocaleString('en-US', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
};

// What a term plan saves against paying monthly, worked out from the amounts
// the viewer is actually quoted: local prices are configured per country
// instead of converted from USD, so the discount is not the same everywhere —
// the annual plan saves 66% on the USD prices but 54% in MXN and 45% in THB. A
// written percentage (the design's `save 60%`) would overstate it nearly
// everywhere. Rounded down, so the badge never claims more than the prices
// deliver; a term that saves nothing gets no badge.
const getSavingsPercent = (
	termPrice: string,
	monthlyPrice: string,
	months: number,
): string | undefined => {
	const term = toAmount(termPrice);
	const monthly = toAmount(monthlyPrice);

	if (!term || !monthly) return undefined;

	const saved = Math.floor((1 - term / (monthly * months)) * 100);

	return saved > 0 ? String(saved) : undefined;
};

/**
 * The non-Tier-1 plan set quoted in the visitor's local currency, or `null`
 * where Solid has no price for the whole multi-currency set — the caller then
 * falls back to the USD plans.
 *
 * The products carry no geo of their own, so the price tables are the gate: a
 * country priced on only some of them is skipped entirely, since the plans it
 * lacks would quote the USD base while Solid charged something else.
 *
 * Not a mirror of the USD set — the multi-currency products are one trial plan
 * plus two no-trial terms, so there is no second trial tier, and both longer
 * terms are advertised per month.
 */
export const getMultiCurrencyPricingPlans = (
	countryCode: string,
	isTrialAvailable = true,
): PricingPlan[] | null => {
	const trial = PRICES_1_MONTH_49_99_TRIAL[countryCode];
	const quarterPrice = PRICES_3_MONTH_89_99[countryCode];
	const yearPrice = PRICES_1_YEAR_199_99[countryCode];
	const symbol = CURRENCY_SYMBOLS[COUNTRY_CURRENCIES[countryCode]];

	if (!trial || !quarterPrice || !yearPrice || !symbol) return null;

	const [trialPrice, monthPrice] = trial;
	const quote = (price: string) => `${symbol}${price}`;
	const savedOnYear = getSavingsPercent(yearPrice, monthPrice, 12);

	const monthlyPlan: PricingPlan = isTrialAvailable
		? {
				id: 'trial-full',
				productName: MULTI_CURRENCY_PRODUCTS.MONTH_TRIAL,
				label: 'plans_trialFull',
				price: quote(trialPrice),
				badge: 'plans_badgeBestForFirstTimers',
				badgeVariant: 'primary',
				billing: {
					priceToday: quote(trialPrice),
					renewPrice: quote(monthPrice),
					periodKey: 'plansLegal_periodMonth',
					trialDurationKey: 'plansLegal_trial7Day',
				},
		  }
		: {
				id: 'monthly',
				productName: MULTI_CURRENCY_PRODUCTS.MONTH,
				label: 'plans_monthly',
				price: quote(monthPrice),
				badge: 'plans_badgeBestForFirstTimers',
				badgeVariant: 'primary',
				billing: {
					priceToday: quote(monthPrice),
					renewPrice: quote(monthPrice),
					periodKey: 'plansLegal_periodMonth',
				},
		  };

	return [
		monthlyPlan,
		{
			id: 'quarterly',
			productName: MULTI_CURRENCY_PRODUCTS.QUARTER,
			label: 'plans_quarterly',
			price: quote(getPerMonthAmount(quarterPrice, 3)),
			suffix: 'plans_perMonth',
			billing: {
				priceToday: quote(quarterPrice),
				renewPrice: quote(quarterPrice),
				periodKey: 'plansLegal_period3Months',
			},
		},
		{
			id: 'annual',
			productName: MULTI_CURRENCY_PRODUCTS.YEAR,
			label: 'plans_annualShort',
			price: quote(getPerMonthAmount(yearPrice, 12)),
			suffix: 'plans_perMonth',
			badge: savedOnYear ? 'plans_badgeSavePercent' : undefined,
			badgeVariant: 'save',
			badgePercent: savedOnYear,
			billing: {
				priceToday: quote(yearPrice),
				renewPrice: quote(yearPrice),
				periodKey: 'plansLegal_periodYear',
			},
		},
	];
};

const USD_BASE_PRICES = {
	MONTH: '49.99',
	QUARTER: '89.99',
	YEAR: '199.99',
} as const;

export const getDefaultPricingPlans = (countryCode: string): PricingPlan[] => {
	const trial = PRICES_1_MONTH_49_99_TRIAL[countryCode];
	const localQuarter = PRICES_3_MONTH_89_99[countryCode];
	const localYear = PRICES_1_YEAR_199_99[countryCode];
	const localSymbol = CURRENCY_SYMBOLS[COUNTRY_CURRENCIES[countryCode]];
	const isLocal = !!(trial && localQuarter && localYear && localSymbol);

	const symbol = isLocal ? localSymbol : CURRENCY_SYMBOLS.USD;
	const monthPrice = isLocal ? trial[1] : USD_BASE_PRICES.MONTH;
	const quarterPrice = isLocal ? localQuarter : USD_BASE_PRICES.QUARTER;
	const yearPrice = isLocal ? localYear : USD_BASE_PRICES.YEAR;
	const quote = (price: string) => `${symbol}${price}`;

	const termPlan = (
		id: string,
		productName: string,
		label: string,
		price: string,
		periodKey: string,
		renewKey: string,
	): PricingPlan => ({
		id,
		productName,
		label,
		price: quote(price),
		billing: {
			priceToday: quote(price),
			renewPrice: quote(price),
			periodKey,
		},
		note: {
			price: quote(price),
			renewKey,
			renewPrice: quote(price),
		},
	});

	return [
		termPlan(
			'monthly',
			MULTI_CURRENCY_PRODUCTS.MONTH,
			'plansDefault_monthly',
			monthPrice,
			'plansLegal_periodMonth',
			'plansDefault_everyMonth',
		),
		termPlan(
			'quarterly',
			MULTI_CURRENCY_PRODUCTS.QUARTER,
			'plansDefault_quarterly',
			quarterPrice,
			'plansLegal_period3Months',
			'plansDefault_every3Months',
		),
		termPlan(
			'annual',
			MULTI_CURRENCY_PRODUCTS.YEAR,
			'plansDefault_annual',
			yearPrice,
			'plansLegal_periodYear',
			'plansDefault_everyYear',
		),
	];
};
